/**
 * <return-form> — wires the return form to the Atomic ecom middleware
 * via the shared AtomicApi client.
 *
 * The form posts { orderId, email } and the client auto-injects `locale`, so the
 * middleware receives { orderId, email, locale } (e.g. locale: "cs-cz"). The
 * endpoint path is read from the `data-endpoint` attribute so it is easy to repoint.
 *
 * Markup contract (per <form data-return-form>):
 *   [name="email"]          — email input
 *   [name="order_number"]   — order number input (sent as orderId)
 *   button[type="submit"]   — submit button (disabled while loading)
 *   [data-return-status]    — status message target (success / error text)
 *   [data-return-result]    — success result target (the returned URL is rendered here)
 */
class ReturnForm extends HTMLElement {
  constructor() {
    super();
    this.endpoint = this.dataset.endpoint || '/orders/validate-return';
    this.strings = window.returnFormStrings || {};
    this.iconTemplate = this.querySelector('template[data-result-icon]');
    this.handleSubmit = this.handleSubmit.bind(this);
    this.forms = Array.from(this.querySelectorAll('form[data-return-form]'));
  }

  connectedCallback() {
    this.forms.forEach((form) => form.addEventListener('submit', this.handleSubmit));
  }

  disconnectedCallback() {
    this.forms.forEach((form) => form.removeEventListener('submit', this.handleSubmit));
  }

  async handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.dataset.loading === 'true') return;

    // The clicked button drives the loading state and which endpoint we hit.
    // Declare → /orders/validate-return, Find my return → /orders/manage-return.
    // Both are middleware paths returning a ready returnUrl. Each button carries
    // its own data-endpoint; only fall back to the default when it has none.
    const button = event.submitter || form.querySelector('button[type="submit"]');
    const endpoint =
      button && button.hasAttribute('data-endpoint') ? button.dataset.endpoint : this.endpoint;

    const email = this.fieldValue(form, 'email');
    const orderId = this.fieldValue(form, 'order_number');

    this.clearResult(form);
    this.setStatus(form, '');

    if (!this.validate(form, email, orderId)) return;

    if (!endpoint) {
      this.setStatus(form, this.strings.error, 'error');
      return;
    }

    this.setLoading(form, button, true);

    // Both endpoints take { orderId, email, locale } (locale auto-injected).
    const response = await window.AtomicApi.post(endpoint, { orderId, email });

    this.setLoading(form, button, false);

    if (!response.ok) {
      this.setStatus(form, this.errorMessage(response, email, orderId), 'error');
      return;
    }

    this.handleSuccess(form, response.data);
  }

  /**
   * Maps a failed response to customer-facing copy. The middleware's contract
   * (AAS-886) distinguishes the two "we looked and it's a no" cases:
   *
   *   404 — no such order for that email/order-number pair
   *   409 — the order exists but is not in a returnable state. Observed on qual
   *         as {"code":"ORDER_NOT_SHIPPED"}; the body carries a machine-readable
   *         `code`, so this can be split per reason later if more 409s appear.
   *   200 — returnable; handled by handleSuccess()
   *
   * 400/422 are treated like a 404: the middleware rejects a malformed order
   * number that way, and "no order found" is the actionable read for the
   * customer. Everything else — network, timeout, 5xx, bad JSON, missing
   * config — is technical, so it gets the generic "try again" copy. Never
   * blame the customer's input for our own outage.
   */
  errorMessage(response, email, orderId) {
    if (response.status === 409) {
      return this.interpolate(this.strings.notReturnable || this.strings.error, email, orderId);
    }

    const isNoMatch =
      response.status === 404 || response.status === 400 || response.status === 422;

    if (isNoMatch) {
      return this.interpolate(this.strings.notFound || this.strings.error, email, orderId);
    }

    return this.strings.error || '';
  }

  /** Fills the [email] / [order_number] placeholders used in the translations. */
  interpolate(template, email, orderId) {
    return (template || '')
      .replace(/\[email\]/g, email)
      .replace(/\[order_number\]/g, orderId);
  }

  /**
   * Success handler — both endpoints return a ready URL built server-side:
   * validate-return → `returnUrl` (step1), manage-return → `manageReturnUrl`
   * (track-all). Open it.
   */
  handleSuccess(form, data) {
    const url =
      (data && (data.returnUrl || data.manageReturnUrl || data.url || data.redirectUrl || data.link)) || '';
    this.setStatus(form, this.strings.success, 'success');

    if (!url) return;

    // Open the returned URL immediately so the customer doesn't have to click again.
    // A window.open() that runs after the awaited fetch can be blocked by popup
    // blockers, so fall back to a same-tab redirect when that happens.
    const opened = window.open(url, '_blank');
    if (opened) {
      opened.opener = null;
    } else {
      window.location.assign(url);
      return;
    }

    // Leave a manual link too, as a last resort if the new tab was suppressed.
    const target = form.querySelector('[data-return-result]');
    if (!target) return;

    const link = document.createElement('a');
    link.href = url;
    link.textContent = this.strings.continue || url;
    link.className = 'return-form__result-link';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    // Trailing redirect icon (↗), cloned from the section's <template>.
    if (this.iconTemplate) {
      link.appendChild(this.iconTemplate.content.cloneNode(true));
    }

    target.replaceChildren(link);
  }

  fieldValue(form, name) {
    const field = form.querySelector(`[name="${name}"]`);
    return field ? field.value.trim() : '';
  }

  /**
   * Inline per-field validation. Renders a message under each invalid field and
   * focuses the first one. Returns true when everything is valid.
   */
  validate(form, email, orderId) {
    let firstInvalid = null;

    let emailError = '';
    if (!email) emailError = this.strings.emailRequired || this.strings.error;
    else if (!this.isValidEmail(email)) emailError = this.strings.emailInvalid || this.strings.error;
    this.setFieldError(form, 'email', emailError);
    if (emailError) firstInvalid = firstInvalid || 'email';

    const orderError = orderId ? '' : this.strings.orderRequired || this.strings.error;
    this.setFieldError(form, 'order_number', orderError);
    if (orderError) firstInvalid = firstInvalid || 'order_number';

    if (firstInvalid) {
      const input = form.querySelector(`[name="${firstInvalid}"]`);
      if (input) input.focus();
      return false;
    }
    return true;
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  setFieldError(form, name, message) {
    const target = form.querySelector(`[data-error-for="${name}"]`);
    const input = form.querySelector(`[name="${name}"]`);
    if (target) {
      target.textContent = message || '';
      target.hidden = !message;
    }
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  setLoading(form, button, isLoading) {
    form.dataset.loading = isLoading ? 'true' : 'false';
    if (!button) return;

    button.disabled = isLoading;
    if (isLoading) {
      button.dataset.label = button.textContent;
      if (this.strings.loading) button.textContent = this.strings.loading;
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
  }

  setStatus(form, message, type) {
    const status = form.querySelector('[data-return-status]');
    if (!status) return;

    status.textContent = message || '';
    status.classList.toggle('return-form__status--error', type === 'error');
    status.classList.toggle('return-form__status--success', type === 'success');

    if (message && type === 'error') status.setAttribute('role', 'alert');
    else status.removeAttribute('role');
  }

  clearResult(form) {
    const target = form.querySelector('[data-return-result]');
    if (target) target.replaceChildren();
  }
}

if (!customElements.get('return-form')) {
  customElements.define('return-form', ReturnForm);
}
